import { AlertCircle } from 'lucide-react'
import { useTranslation } from '@kostbar/i18n'
import type { PricingItem, ValidationError } from '../types'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { ResponsiveTableWrapper } from '@/shared/components/responsive-table-wrapper'

interface PricingPreviewTableProps {
  items: Array<PricingItem>
  errors: Array<ValidationError>
}

export function PricingPreviewTable({
  items,
  errors,
}: PricingPreviewTableProps) {
  const { t: tPricing } = useTranslation('pricing')

  const getRowErrors = (index: number) => {
    return errors.filter((e) => e.row === index)
  }

  if (items.length === 0) {
    return (
      <div className="text-center py-12 text-muted-foreground">
        {tPricing('previewTable.empty')}
      </div>
    )
  }

  return (
    <div className="rounded-md border">
      <ResponsiveTableWrapper>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-12">
                {tPricing('previewTable.rowNumber')}
              </TableHead>
              <TableHead>{tPricing('previewTable.sku')}</TableHead>
              <TableHead>{tPricing('previewTable.price')}</TableHead>
              <TableHead className="w-12"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.map((item, index) => {
              const rowErrors = getRowErrors(index)
              const hasErrors = rowErrors.length > 0

              return (
                <TableRow
                  key={index}
                  className={hasErrors ? 'bg-destructive/10' : ''}
                >
                  <TableCell className="font-mono text-sm">
                    {index + 1}
                  </TableCell>
                  <TableCell className="font-mono">{item.sku}</TableCell>
                  <TableCell>
                    € {(item.priceGrossCents / 100).toFixed(2)}
                  </TableCell>
                  <TableCell>
                    {hasErrors && (
                      <div className="group relative">
                        <AlertCircle className="w-4 h-4 text-destructive" />
                        <div className="absolute right-0 top-full mt-1 w-64 p-2 bg-popover border rounded-md shadow-md text-xs z-10 hidden group-hover:block">
                          {rowErrors.map((err, i) => (
                            <div key={i} className="text-destructive">
                              <span className="font-medium">{err.field}:</span>{' '}
                              {err.message}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </TableCell>
                </TableRow>
              )
            })}
          </TableBody>
        </Table>
      </ResponsiveTableWrapper>
    </div>
  )
}
